import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '@/lib/api/client';
import { useWorkspaceStore } from '@/stores/workspace-store';

/**
 * `syncConfig` of GET /integrations/:provider — what every sync wizard restores
 * its form from, and what the dashboards summarise. The API's contract is
 * ProviderSyncConfigEmbeddedDto; lib/__tests__/integration-sync-config-matches-the-api.guard.spec.ts
 * keeps the two in step (wave 3, ingest-4 item 7: the API omitted selectedOUs,
 * selectedDNs, includeSuspended, includeDisabled and duplicateDetectionFields,
 * so a saved setting reopened unset and the LDAP dashboard read the DNs of
 * nothing).
 */
export type IntegrationSyncConfig = {
  id: string;
  selectedGroups: string[];
  /** Google: the saved OU paths. */
  selectedOUs?: string[];
  /** LDAP: the saved group DNs. */
  selectedDNs?: string[];
  includeAllUsers: boolean;
  fieldMappings: Array<{ sourceField: string; targetField: string; transform?: string }>;
  duplicateHandling: string;
  duplicateDetectionFields: string[];
  protectedFields: string[];
  deactivateMissing: boolean;
  /** Google, Okta — absent when never saved. */
  includeSuspended?: boolean;
  /** LDAP — absent when never saved. */
  includeDisabled?: boolean;
  providerOptions: Record<string, unknown>;
  clearFieldsEmptiedUpstream: boolean;
  requireApprovalForFirstSync: boolean;
  alwaysStageForReview: boolean;
  firstSyncApprovedAt: string | null;
};

export interface IntegrationConfig {
  provider: string;
  isConnected: boolean;
  connectedAt: string | null;
  syncSchedule: string | null;
  lastSyncAt: string | null;
  syncStatus: string | null;
  totalSynced: number;
  connectionDetails: Record<string, unknown>;
  syncConfig: IntegrationSyncConfig;
  schedule: Record<string, unknown>;
  // Provider-specific fields (Google, M365, LDAP, Okta)
  connection?: Record<string, unknown>;
  organizationalUnits?: unknown[];
  users?: unknown[];
  groups?: unknown[];
  syncHistory?: unknown[];
  fieldMappings?: Record<string, string>;
  [key: string]: unknown;
}

export interface IntegrationConnectionPayload {
  credentials: Record<string, string>;
  [key: string]: unknown;
}

export interface IntegrationSchedulePayload {
  isEnabled: boolean;
  frequency: string;
  preferredHour?: number;
  [key: string]: unknown;
}

export interface IntegrationSyncPayload {
  provider: string;
  data?: Record<string, unknown>;
}

export interface IntegrationSyncConfigPayload {
  selectedGroups?: string[];
  includeAllUsers?: boolean;
  fieldMappings?:
    | Record<string, string>
    | Array<{ sourceField: string; targetField: string }>;
  [key: string]: unknown;
}

// Mode-keyed (2026-06-11): demo and production integration connection state
// must not collide — a demo session reads the demo tenant's connector, the
// real session reads the real one.
export const integrationKeys = {
  all: () => ['integrations'] as const,
  provider: (provider: string) =>
    [...integrationKeys.all(), provider] as const,
};

export function useIntegration(provider: string) {
  // SI5 (audit 2026-09-25) — scoped by the active workspace (the one
  // `x-workspace-id` carries). After a switch the sync wizards remount, and an
  // unscoped key handed the new mount the PREVIOUS workspace's cached row to
  // seed from (connected, its saved selections) before the refetch landed —
  // the FE7 lesson. Prefix invalidation (`integrationKeys.provider`) still
  // matches.
  const workspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);
  return useQuery({
    queryKey: [...integrationKeys.provider(provider), workspaceId] as const,
    queryFn: () => fetchApi<IntegrationConfig>(`/integrations/${provider}`),
    enabled: !!provider,
    refetchInterval: (q) => {
      const data = q.state.data as IntegrationConfig | undefined;
      const rows = Array.isArray(data?.syncHistory) ? data!.syncHistory : [];
      const anyRunning = rows.some((r: unknown) => {
        const status = (r as { status?: string } | null)?.status;
        return (
          status === 'Running' ||
          status === 'Pending' ||
          status === 'In Progress'
        );
      });
      return anyRunning ? 5_000 : false;
    },
    refetchIntervalInBackground: false,
  });
}

export function useConnectIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, data }: { provider: string; data: IntegrationConnectionPayload }) =>
      fetchApi<IntegrationConfig>(`/integrations/${provider}/connect`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: integrationKeys.provider(vars.provider) }),
  });
}

export function useDisconnectIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (provider: string) =>
      fetchApi<{ success: boolean }>(`/integrations/${provider}/disconnect`, { method: 'POST' }),
    onSuccess: (_, provider) => qc.invalidateQueries({ queryKey: integrationKeys.provider(provider) }),
  });
}

export function useSyncIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: string | IntegrationSyncPayload) => {
      const provider = typeof input === 'string' ? input : input.provider;
      const data = typeof input === 'string' ? undefined : input.data;
      return fetchApi<{ jobId: string; status: string; domainId?: string; syncConfigId?: string }>(`/integrations/${provider}/sync`, {
        method: 'POST',
        body: data ? JSON.stringify(data) : undefined,
      });
    },
    onSuccess: (_, input) => {
      const provider = typeof input === 'string' ? input : input.provider;
      qc.invalidateQueries({ queryKey: integrationKeys.provider(provider) });
    },
  });
}

export function useUpdateIntegrationSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, data }: { provider: string; data: IntegrationSchedulePayload }) =>
      fetchApi<IntegrationConfig>(`/integrations/${provider}/schedule`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: integrationKeys.provider(vars.provider) }),
  });
}

export function useUpdateIntegrationSyncConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, data }: { provider: string; data: IntegrationSyncConfigPayload }) =>
      fetchApi<IntegrationConfig>(`/integrations/${provider}/sync-config`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: integrationKeys.provider(vars.provider) }),
  });
}
